(() => {
  const APPROACH_SELECTOR = '[data-sb-media-load="approach"]';
  const CONTINUOUS_ATTRIBUTE = 'data-sb-media-observe-continuously';
  const APPROACH_ROOT_MARGIN = '1000px 1000px';
  const LIFECYCLE_ROOT_MARGIN = '200px 0px';
  const observedRoots = new WeakSet();
  const revealFrames = new WeakSet();

  // ---------------------------------------------------------------------------
  // Autoplay-loop lifecycle
  //
  // Every muted looping video on the site (PDP hero, gallery renders, large
  // feature, feature carousel) shares one lifecycle owner instead of each
  // section hand-rolling its own. User-initiated playback (thumbnail hover,
  // story player, click-to-play carousels) stays out of it on purpose.
  //
  // The lifecycle exists because of hard-won Safari lessons (2026-08-09):
  // - A <video> created inside a <template> document can return from a
  //   background tab with a permanently dead decode pipeline: currentTime
  //   advances, readyState says 4, no media error is raised, and load() does
  //   NOT revive it - only a brand-new element born in the live document does
  //   (all measured on-page). Hence adoption: template-born elements are
  //   re-created in the live document before being wired up.
  // - getVideoPlaybackQuality().totalVideoFrames LIES on freshly created
  //   Safari elements (reports frozen while visibly animating), so element
  //   health can only be judged by frames actually presented via
  //   requestVideoFrameCallback. Hence the watchdog below.
  // - WebKit budgets a small number of simultaneous decoders per page and
  //   pausing does not return one - only detaching the source does. Hence the
  //   'release' offscreen mode.
  // - The native autoplay flag is what makes the browser resume playback by
  //   itself when a tab comes back; a video driven only by a JS play() the
  //   browser never re-issues comes back dead.
  // ---------------------------------------------------------------------------

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shouldAutoplay = () => !reducedMotionQuery.matches && !navigator.connection?.saveData;

  // video -> { hooks, offscreen, src, revealed, controller }
  const lifecycleRecords = new WeakMap();
  // Registered videos currently near the viewport, so the visibilitychange
  // handler knows which ones to resume. Disconnected elements are dropped
  // lazily by the observer callback and the resume loop.
  const visibleVideos = new Set();
  let lifecycleObserver = null;

  const reveal = (video) => {
    const record = lifecycleRecords.get(video);
    if (!record || record.revealed || !shouldAutoplay()) return;
    record.revealed = true;
    record.hooks.reveal?.(video);
  };

  const conceal = (video) => {
    const record = lifecycleRecords.get(video);
    if (!record) return;
    record.revealed = false;
    record.hooks.conceal?.(video);
  };

  const pauseVideo = (video) => {
    video.autoplay = false;
    video.pause();
    conceal(video);
  };

  const playVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!shouldAutoplay()) {
      pauseVideo(video);
      return;
    }
    const record = lifecycleRecords.get(video);
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      // If playback cannot start, show the poster again rather than leave an
      // empty <video> sitting over its own background - without this a refused
      // play() is indistinguishable from a broken tile.
      playPromise.catch(() => {
        if (lifecycleRecords.get(video) === record) conceal(video);
      });
    }
  };

  const createLiveVideoElement = (video) => {
    const fresh = document.createElement('video');
    Array.from(video.attributes).forEach(({ name, value }) => {
      if (
        name === 'data-sb-media-watchdog'
        || name === 'data-sb-media-rebuilds'
        || name === 'data-sb-media-lifecycle'
      ) return;
      fresh.setAttribute(name, value);
    });
    Array.from(video.children).forEach((child) => {
      if (child instanceof HTMLSourceElement) fresh.appendChild(child.cloneNode(true));
    });
    fresh.dataset.sbMediaAdopted = 'true';
    fresh.muted = true;
    if (!fresh.getAttribute('src') && fresh.dataset.sbMediaSrc) {
      fresh.setAttribute('src', fresh.dataset.sbMediaSrc);
    }
    return fresh;
  };

  const adopt = (video) => {
    if (!(video instanceof HTMLVideoElement)) return video;
    if (video.dataset.sbMediaAdopted === 'true') return video;
    const fresh = createLiveVideoElement(video);
    video.replaceWith(fresh);
    return fresh;
  };

  const releaseVideo = (video) => {
    video.pause();
    if (!video.getAttribute('src')) return;
    video.removeAttribute('src');
    video.load();
    // Drop the reveal state so the poster is shown again while detached;
    // without this the consumer keeps revealing an empty <video>.
    conceal(video);
  };

  const restoreVideo = (video) => {
    if (!shouldAutoplay()) {
      pauseVideo(video);
      return;
    }
    video.autoplay = true;
    const record = lifecycleRecords.get(video);
    const source = record?.src || video.dataset.sbMediaSrc || '';
    if (source && !video.getAttribute('src')) {
      video.setAttribute('src', source);
      video.load();
    }
    playVideo(video);
  };

  const unregisterVideo = (video) => {
    const record = lifecycleRecords.get(video);
    if (!record) return;
    record.controller.abort();
    pauseVideo(video);
    lifecycleObserver?.unobserve(video);
    visibleVideos.delete(video);
    lifecycleRecords.delete(video);
    delete video.dataset.sbMediaLifecycle;
    delete video.dataset.sbMediaWatchdog;
    delete video.dataset.sbMediaRebuilds;
  };

  // Health = "a frame actually reached the screen". At most one rebuild per
  // incident; a fresh element that still presents nothing falls back to the
  // poster, which the reveal path re-hides by itself if a frame ever shows.
  // The watchdog fires zero times on a healthy page - it is deliberate
  // insurance against Safari handing back an element with a dead decoder.
  const ensureDecodeAlive = (video) => {
    const record = lifecycleRecords.get(video);
    if (!record || !shouldAutoplay()) return;
    if (typeof video.requestVideoFrameCallback !== 'function') return;
    if (video.dataset.sbMediaWatchdog === 'armed') return;
    video.dataset.sbMediaWatchdog = 'armed';
    let presented = false;
    video.requestVideoFrameCallback(() => {
      if (lifecycleRecords.get(video) !== record) return;
      presented = true;
      delete video.dataset.sbMediaWatchdog;
      delete video.dataset.sbMediaRebuilds;
    });
    window.setTimeout(() => {
      if (lifecycleRecords.get(video) !== record) return;
      delete video.dataset.sbMediaWatchdog;
      if (presented || document.hidden || !video.isConnected || video.paused) return;
      conceal(video);
      if (Number(video.dataset.sbMediaRebuilds || 0) >= 1) return;
      const fresh = createLiveVideoElement(video);
      fresh.dataset.sbMediaRebuilds = '1';
      lifecycleObserver?.unobserve(video);
      visibleVideos.delete(video);
      // Pending callbacks on the old element must not mutate the replacement's record.
      record.controller.abort();
      lifecycleRecords.delete(video);
      video.replaceWith(fresh);
      wireVideo(fresh, record);
      record.hooks.replaced?.(fresh, video);
      ensureDecodeAlive(fresh);
    }, 1500);
  };

  const getLifecycleObserver = () => {
    if (lifecycleObserver || !('IntersectionObserver' in window)) return lifecycleObserver;
    lifecycleObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (!(video instanceof HTMLVideoElement)) return;
        const record = lifecycleRecords.get(video);
        if (!record || !video.isConnected) {
          visibleVideos.delete(video);
          lifecycleObserver.unobserve(video);
          return;
        }
        if (entry.isIntersecting) {
          visibleVideos.add(video);
          restoreVideo(video);
          return;
        }
        visibleVideos.delete(video);
        if (record.offscreen === 'release') {
          releaseVideo(video);
        } else if (record.offscreen === 'pause') {
          video.pause();
        }
      });
    }, { rootMargin: LIFECYCLE_ROOT_MARGIN });
    return lifecycleObserver;
  };

  const wireVideo = (video, record) => {
    record.controller = new AbortController();
    const { signal } = record.controller;
    lifecycleRecords.set(video, record);
    video.dataset.sbMediaLifecycle = 'true';
    video.autoplay = shouldAutoplay();

    if (record.hooks.reveal) {
      const markReady = () => {
        if (lifecycleRecords.get(video) !== record) return;
        if (video.paused || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
        reveal(video);
      };
      const revealAfterPresentedFrame = () => {
        if (lifecycleRecords.get(video)?.revealed) return;
        if (typeof video.requestVideoFrameCallback === 'function') {
          video.requestVideoFrameCallback(() => markReady());
          return;
        }
        markReady();
      };
      video.addEventListener('playing', revealAfterPresentedFrame, { signal });
      video.addEventListener('error', () => conceal(video), { signal });
      video.addEventListener('timeupdate', () => {
        if (video.currentTime > 0 && !video.paused) revealAfterPresentedFrame();
      }, { signal });
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !video.paused) {
        revealAfterPresentedFrame();
      }
    }

    const observer = getLifecycleObserver();
    if (observer) {
      observer.observe(video);
    } else {
      visibleVideos.add(video);
      restoreVideo(video);
    }
  };

  const registerVideo = (video, options = {}) => {
    if (!(video instanceof HTMLVideoElement)) return video;
    const target = options.adopt ? adopt(video) : video;
    if (target.dataset.sbMediaLifecycle === 'true') return target;

    const source = target.getAttribute('src') || target.dataset.sbMediaSrc || '';
    if (source) target.dataset.sbMediaSrc = source;
    // 'release' hands the decoder back while offscreen and needs a remembered
    // src attribute to re-attach; videos fed by <source> children can only
    // pause.
    let offscreen = options.offscreen || target.dataset.sbMediaOffscreen || 'none';
    if (offscreen === 'release' && !source) offscreen = 'pause';

    const record = {
      hooks: options.hooks || {},
      offscreen,
      src: source,
      revealed: false,
    };
    // Poster until a frame is actually presented.
    record.hooks.conceal?.(target);
    wireVideo(target, record);
    // No decoded metadata does not mean no download: preserve the browser's
    // in-flight preload instead of aborting it and requesting the video again.
    if (shouldAutoplay() && target.readyState === HTMLMediaElement.HAVE_NOTHING
      && target.networkState === HTMLMediaElement.NETWORK_EMPTY) target.load();
    return target;
  };

  // Backgrounding the tab is the other way playback dies, and it needs no help
  // going: the browser suspends media in a hidden tab on its own. What matters
  // is the way back - a video that never left the viewport gets no
  // intersection change on return, so nothing else would resume it. Resume
  // only, deliberately nothing on hide.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    visibleVideos.forEach((video) => {
      if (!video.isConnected) {
        visibleVideos.delete(video);
        return;
      }
      restoreVideo(video);
      ensureDecodeAlive(video);
    });
  });

  const syncAutoplayPreference = () => {
    document.querySelectorAll('video[data-sb-media-lifecycle]').forEach((video) => {
      if (!lifecycleRecords.has(video)) return;
      if (!shouldAutoplay()) pauseVideo(video);
      else if (visibleVideos.has(video)) restoreVideo(video);
    });
  };
  reducedMotionQuery.addEventListener('change', syncAutoplayPreference);
  navigator.connection?.addEventListener?.('change', syncAutoplayPreference);

  // ---------------------------------------------------------------------------
  // Approach loading (lazy src attach when a root nears the viewport)
  // ---------------------------------------------------------------------------

  const activate = (root) => {
    if (!(root instanceof Element)) return;

    root.querySelectorAll('[data-sb-media-src]').forEach((media) => {
      if (media instanceof HTMLVideoElement && media.dataset.sbMediaLifecycle === 'true') return;
      const source = (media.getAttribute('data-sb-media-src') || '').trim();
      if (!source || media.getAttribute('src') === source) return;
      if (media instanceof HTMLIFrameElement && media.hasAttribute('data-sb-media-reveal-on-load')) {
        media.removeAttribute('data-sb-media-loaded');
        if (!revealFrames.has(media)) {
          revealFrames.add(media);
          media.addEventListener('load', () => {
            // The initial empty iframe can load before its deferred document.
            if (!media.getAttribute('src') || media.contentDocument?.URL === 'about:blank') return;
            media.setAttribute('data-sb-media-loaded', 'true');
          });
        }
      }
      media.setAttribute('src', source);
    });

    root.querySelectorAll('video[data-sb-media-autoplay]').forEach((video) => registerVideo(video));
    root.setAttribute('data-sb-media-activated', 'true');
    root.dispatchEvent(new CustomEvent('sb:media:activated'));
  };

  const setApproachState = (root, isInRange) => {
    if (!(root instanceof Element)) return;

    if (isInRange) {
      root.setAttribute('data-sb-media-in-range', 'true');
      activate(root);
      root.dispatchEvent(new CustomEvent('sb:media:approach-enter'));
      return;
    }

    root.removeAttribute('data-sb-media-in-range');
    root.dispatchEvent(new CustomEvent('sb:media:approach-leave'));
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const root = entry.target;
          setApproachState(root, entry.isIntersecting);
          if (entry.isIntersecting && !root.hasAttribute(CONTINUOUS_ATTRIBUTE)) {
            observer.unobserve(root);
          }
        });
      },
      { rootMargin: APPROACH_ROOT_MARGIN, threshold: 0 }
    )
    : null;

  const observe = (root) => {
    if (!(root instanceof Element) || observedRoots.has(root)) return;
    observedRoots.add(root);

    if (observer) {
      observer.observe(root);
      return;
    }

    setApproachState(root, true);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('video[data-sb-media-poster-video]').forEach(video => {
      registerVideo(video, {
        offscreen: 'pause',
        hooks: {
          reveal: target => target.classList.add('is-frame-ready'),
          conceal: target => target.classList.remove('is-frame-ready'),
        },
      });
    });
    if (scope instanceof Element && scope.matches(APPROACH_SELECTOR)) {
      observe(scope);
    }
    scope.querySelectorAll?.(APPROACH_SELECTOR).forEach(observe);
  };

  window.SBMediaLoader = {
    activate,
    initialize,
    rootMargin: APPROACH_ROOT_MARGIN,
    video: {
      adopt,
      register: registerVideo,
      unregister: unregisterVideo,
      play: playVideo,
      conceal,
      shouldAutoplay,
    },
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    const unobserve = (root) => {
      observer?.unobserve(root);
      observedRoots.delete(root);
    };
    if (scope instanceof Element && scope.matches(APPROACH_SELECTOR)) unobserve(scope);
    scope.querySelectorAll?.(APPROACH_SELECTOR).forEach(unobserve);
    if (scope instanceof HTMLVideoElement) unregisterVideo(scope);
    scope.querySelectorAll?.('video[data-sb-media-lifecycle]').forEach(unregisterVideo);
  });
})();
